import { useCallback, useEffect, useMemo, useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import moment from 'moment';
import { apiEndpoints, downloadURI } from '@selfserve/react-utils';
import { useCustomSearchParams } from '@selfserve/react-hooks';
import {
  partnerProperties,
  defaultPartnerProperies,
  STATUS_VALUE,
} from '../constants/partner-properties';
import { partnerStatusesTexts } from '../constants/status-texts';
import { partnerStatusColors } from '../constants/status-colors';
import { AdminSearchInput } from './admin-search-input';
import {
  StyledExportButton,
  StyledStatus,
  StyledTable,
  StyledTd,
  StyledTr,
} from '../ui/admin-styled-components';
import { AdminPaginationController } from './admin-pagination-controller';
import { AdminPropertyFilter } from './admin-property-filter';
import { AdminStatusFilter } from './admin-status-filter';
import { TableHeaderButton } from '../ui/table-header-button';
import { BeSearchButton } from '../ui/be-search-button';

const StyledPartnersListContainer = styled('div')`
  height: calc(100% - 110px);
  margin-top: 24px;
`;

const StyledFiltersContainer = styled('div')`
  width: 100%;
  display: flex;
`;

const StyledDropdownContainer = styled('div')`
  margin-left: 16px;
`;

const StyledTrackHorizontal = styled('div')`
  height: 10px !important;
  z-index: 2;
  background-color: #fff;
  bottom: 0;
  width: 100%;
  div {
    height: 6px !important;
    margin-top: 4px;
  }
`;

const StyledTrackVertical = styled('div')`
  width: 10px !important;
  z-index: 2;
  background-color: #fff;
  right: 0;
  height: 100%;
  div {
    width: 6px !important;
    margin-left: 4px;
  }
`;

const StyledScrollbarsContainer = styled('div')`
  margin-top: 24px;
  height: calc(100% - 32px);
`;

const StyledRateContainer = styled('div')`
  display: flex;
`;

const StyledRate = styled('div')`
  width: 20px;
  height: 20px;
  background: no-repeat url('assets/star.svg') center;
  & + & {
    margin-left: 8px;
  }
`;

interface Props {
  partners: any[];
  handleSetBeFilter: (value: string) => boolean;
}

export const AdminPartnersList = ({ partners, handleSetBeFilter }: Props) => {
  const { t } = useTranslation();
  const [searchByBe, setSearchByBe] = useState(false);
  const [query] = useCustomSearchParams();
  const [search, setSearch] = useState('');
  const [columns, setColumns] = useState(defaultPartnerProperies);

  useEffect(() => {
    const searchValue = query['search'] as string;
    if (searchValue) {
      const isSearchByBe = handleSetBeFilter(searchValue);
      setSearchByBe(isSearchByBe);
    }
  }, []);

  useEffect(() => {
    if (searchByBe && search !== query['search']) setSearchByBe(false);
  }, [search]);

  const handleDownloadCsv = () => downloadURI(apiEndpoints.downloadPartnersCsv);

  const handleSearchButtonClick = useCallback(() => {
    const isSearchByBe = handleSetBeFilter(search);
    setSearchByBe(isSearchByBe);
  }, [search]);

  const finalizedList = useMemo(
    () =>
      partners?.filter(({ name, pid, Status, Email }: any) => {
        const searchValue = search?.trim()?.toLowerCase();
        if (search && !searchByBe) {
          return (
            name?.toLowerCase()?.includes(searchValue) ||
            Email?.toLowerCase()?.includes(searchValue) ||
            String(pid)?.includes(searchValue) ||
            String(Status)?.includes(searchValue) ||
            partnerStatusesTexts[Status]?.toLowerCase()?.includes(searchValue)
          );
        }
        return true;
      }),
    [partners, search, searchByBe]
  );

  return (
    <StyledPartnersListContainer>
      <StyledFiltersContainer>
        <AdminSearchInput value={search} onChange={setSearch} />
        <BeSearchButton onClick={handleSearchButtonClick} />
        <StyledDropdownContainer>
          <AdminPropertyFilter
            properties={partnerProperties}
            onChange={setColumns}
            value={columns}
          />
        </StyledDropdownContainer>
        <StyledExportButton withoutMargin onClick={handleDownloadCsv}>
          {t('common.export')}
        </StyledExportButton>
      </StyledFiltersContainer>
      <StyledScrollbarsContainer>
        <Scrollbars
          universal
          height="100%"
          renderTrackHorizontal={(props) => (
            <StyledTrackHorizontal {...props} />
          )}
          renderTrackVertical={(props) => <StyledTrackVertical {...props} />}
        >
          <StyledTable withoutMargin>
            <thead>
              <StyledTr sticky>
                {columns.map(({ text, value, sortingValue }) => (
                  value === STATUS_VALUE ? (
                    <AdminStatusFilter
                      value={sortingValue || value}
                      statuses={Object.entries(partnerStatusesTexts).map(([value, text]) => ({
                        text,
                        value,
                        className: partnerStatusColors[value],
                      }))}
                    />
                  ) : (
                    <TableHeaderButton
                      key={value}
                      value={sortingValue || value}
                    >
                      {text}
                    </TableHeaderButton>
                  )
                ))}
              </StyledTr>
            </thead>
            <tbody>
              {finalizedList?.map((partner: any) => (
                <StyledTr disabled key={partner.pid}>
                  {columns.map(({ value }, index: number) => (
                    <StyledTd isPairValue={index === 0} key={value}>
                      {(() => {
                        switch (value) {
                          case 'Name':
                            return (
                              <>
                                {partner[value]}
                                <span>{partner.Email}</span>
                              </>
                            );
                          case 'Created at':
                            return moment(partner[value]).format(
                              'MMM DD, YYYY'
                            );
                          case 'Status':
                            return (
                              <StyledStatus
                                className={partnerStatusColors[partner[value]]}
                              >
                                {partnerStatusesTexts[partner[value]]}
                              </StyledStatus>
                            );
                          case 'score': {
                            const score = Math.floor(partner[value] / 3);
                            return score ? (
                              <StyledRateContainer>
                                {Array.from(Array(score).keys()).map((key) => (
                                  <StyledRate key={key} />
                                ))}
                              </StyledRateContainer>
                            ) : (
                              '-'
                            );
                          }
                          default:
                            return partner[value];
                        }
                      })()}
                    </StyledTd>
                  ))}
                </StyledTr>
              ))}
            </tbody>
          </StyledTable>
        </Scrollbars>
      </StyledScrollbarsContainer>
      <AdminPaginationController />
    </StyledPartnersListContainer>
  );
};
